import { useCallback, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import {
  buildOccupied,
  intervalConflicts,
  newSplitGroupId,
  splitAroundOccupied,
  type MsInterval,
} from './scheduleConflict';
import type { BusyInterval, TimeBlock } from './types';
import { blockKeys, useCreateTimeBlock } from './useTimeBlocks';

export interface PendingCapture {
  taskId: number;
  taskTitle: string;
  startsAt: Date;
  endsAt: Date;
  occupied: MsInterval[];
}

/**
 * Window searched for conflicts and for somewhere to resume after a split.
 *
 * From the start of the target day to the end of the next one: a split that
 * cannot finish inside two days is not a plan anyone will follow.
 */
function searchRange(start: Date): { start: Date; end: Date } {
  const from = new Date(start);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + 2);
  return { start: from, end: to };
}

async function loadOccupied(qc: QueryClient, start: Date): Promise<MsInterval[]> {
  const range = searchRange(start);
  const from = range.start.toISOString();
  const to = range.end.toISOString();

  const [blocks, busy] = await Promise.all([
    qc.fetchQuery({
      queryKey: blockKeys.range(from, to),
      queryFn: () =>
        api<TimeBlock[]>(
          `/api/time-blocks?ends_after=${encodeURIComponent(from)}` +
            `&starts_before=${encodeURIComponent(to)}`,
        ),
    }),
    qc.fetchQuery({
      queryKey: ['calendars', 'busy', from, to],
      queryFn: () =>
        api<BusyInterval[]>(
          `/api/calendars/busy?start=${encodeURIComponent(from)}` +
            `&end=${encodeURIComponent(to)}`,
        ),
    }),
  ]);

  return buildOccupied(busy, blocks);
}

/**
 * Place a freshly captured task on the calendar, asking first when it clashes.
 *
 * Typing `>fri 9am` is the same decision as dragging a chip onto nine o'clock,
 * so it has to answer for a clash the same way (overlap or split) rather
 * than quietly stacking a block on top of a meeting.
 */
export function useCapturePlacement(options: { onPlaced?: (taskTitle: string) => void } = {}) {
  const qc = useQueryClient();
  const createBlock = useCreateTimeBlock();
  const [pending, setPending] = useState<PendingCapture | null>(null);
  const { onPlaced } = options;

  const commit = useCallback(
    async (
      taskId: number,
      taskTitle: string,
      segments: { startsAt: Date; endsAt: Date; isContinuation: boolean }[],
    ) => {
      const groupId = segments.length > 1 ? newSplitGroupId() : null;
      for (const seg of segments) {
        await createBlock.mutateAsync({
          task_id: taskId,
          starts_at: seg.startsAt.toISOString(),
          ends_at: seg.endsAt.toISOString(),
          status: 'confirmed',
          origin: 'manual_drag',
          is_continuation: seg.isContinuation,
          ...(groupId ? { split_group_id: groupId } : {}),
        });
      }
      setPending(null);
      onPlaced?.(taskTitle);
    },
    [createBlock, onPlaced],
  );

  /** Schedule it, or raise the conflict prompt when the slot is taken. */
  const place = useCallback(
    async (taskId: number, taskTitle: string, startsAt: Date, endsAt: Date) => {
      let occupied: MsInterval[] = [];
      try {
        occupied = await loadOccupied(qc, startsAt);
      } catch {
        // A calendar we cannot read is not a reason to lose the capture: place
        // it and let the day show the clash.
        await commit(taskId, taskTitle, [{ startsAt, endsAt, isContinuation: false }]);
        return;
      }

      if (!intervalConflicts(startsAt, endsAt, occupied)) {
        await commit(taskId, taskTitle, [{ startsAt, endsAt, isContinuation: false }]);
        return;
      }

      setPending({ taskId, taskTitle, startsAt, endsAt, occupied });
    },
    [commit, qc],
  );

  const overlap = useCallback(() => {
    if (!pending) return;
    void commit(pending.taskId, pending.taskTitle, [
      { startsAt: pending.startsAt, endsAt: pending.endsAt, isContinuation: false },
    ]);
  }, [commit, pending]);

  const split = useCallback(() => {
    if (!pending) return;
    const segments = splitAroundOccupied(
      pending.startsAt,
      pending.endsAt,
      pending.occupied,
      { searchEnd: searchRange(pending.startsAt).end },
    );
    if (!segments) {
      overlap();
      return;
    }
    void commit(pending.taskId, pending.taskTitle, segments).catch(() =>
      toast.error('Could not split block'),
    );
  }, [commit, overlap, pending]);

  const canSplit = pending
    ? splitAroundOccupied(pending.startsAt, pending.endsAt, pending.occupied, {
        searchEnd: searchRange(pending.startsAt).end,
      }) != null
    : false;

  return {
    pending,
    canSplit,
    place,
    overlap,
    split,
    /** Leaves the task in the backlog, it exists, it just has no block. */
    cancel: () => {
      if (pending) toast.show(`Kept "${pending.taskTitle}" in the backlog`);
      setPending(null);
    },
  };
}
